import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  AccountBalance,
  AutoAwesome,
  Calculate,
  FavoriteBorder,
  HomeWork,
  Search,
  Savings,
  Work,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const categoryOptions = [
  'Featured',
  'Housing',
  'Budgeting & Spending',
  'Debt & Financial Pressure',
  'Work, Income & Tax',
  'Investing & Savings',
  'Financial Wellbeing',
];

const toolTypeStyles = {
  'AI Prompt': { label: 'AI Prompt', bg: '#f3e8ff', color: '#6b21a8' },
  'AI Prompt Generator': { label: 'AI Prompt Generator', bg: '#f3e8ff', color: '#6b21a8' },
  Calculator: { label: 'Calculator', bg: '#dcfce7', color: '#166534' },
  'Guide/Calculator': { label: 'Guide/Calculator', bg: '#e0f2fe', color: '#075985' },
};

const featuredTools = [
  {
    title: 'I earn OK money, but still feel broke',
    type: 'AI Prompt',
    category: 'Financial Wellbeing',
    description: 'Generate a powerful AI prompt to help you understand your situation and find clarity.',
    link: '/apps/i-earn-ok-money-but-still-feel-broke',
    image: '/images/apps/meadow.jpg',
  },
  {
    title: 'Compound Interest Calculator',
    type: 'Calculator',
    category: 'Investing & Savings',
    description: 'See how your money can grow over time with the power of compounding.',
    link: '/apps/compound-interest-calculator',
    image: '/images/apps/money-grows-on-trees-kind-of.jpg',
  },
  {
    title: 'Budget Calculator',
    type: 'Calculator',
    category: 'Budgeting & Spending',
    description: 'Plan your income, track spending and take control of your monthly finances.',
    link: '/apps/budget-calculator',
    image: '/images/apps/budget-notepad.jpg',
  },
  {
    title: 'Mortgage Overpayment Calculator',
    type: 'Calculator',
    category: 'Housing',
    description: 'See how overpaying your mortgage can save you interest and time.',
    link: '/apps/mortgage-overpayment-calculator',
    image: '/images/apps/early-mortgage-repayment.jpg',
  },
];

const categories = [
  {
    title: 'Housing',
    icon: HomeWork,
    accent: '#2563eb',
    tools: [
      { title: 'Mortgage Calculator', type: 'Calculator', link: '/apps/mortgage-calculator' },
      { title: 'Mortgage Overpayment', type: 'Calculator', link: '/apps/mortgage-overpayment-calculator' },
    ],
  },
  {
    title: 'Budgeting & Spending',
    icon: Calculate,
    accent: '#059669',
    tools: [
      { title: 'Budget Calculator', type: 'Calculator', link: '/apps/budget-calculator' },
    ],
  },
  {
    title: 'Debt & Financial Pressure',
    icon: AccountBalance,
    accent: '#dc2626',
    tools: [
      { title: 'Credit Card Debt Calculator', type: 'Calculator', link: '/apps/credit-card-compound-debt' },
    ],
  },
  {
    title: 'Work, Income & Tax',
    icon: Work,
    accent: '#7c3aed',
    tools: [
      { title: '£100k Tax Trap Calculator', type: 'Calculator', link: '/apps/100k-125k-personal-allowance-tax-trap' },
    ],
  },
  {
    title: 'Investing & Savings',
    icon: Savings,
    accent: '#16a34a',
    tools: [
      { title: 'Compound Interest Calculator', type: 'Calculator', link: '/apps/compound-interest-calculator' },
    ],
  },
  {
    title: 'Financial Wellbeing',
    icon: FavoriteBorder,
    accent: '#9333ea',
    tools: [
      { title: 'I earn OK money, but still feel broke', type: 'AI Prompt Generator', link: '/apps/i-earn-ok-money-but-still-feel-broke' },
    ],
  },
];

const searchableText = (tool, category = '') =>
  [tool.title, tool.description, tool.category, tool.type, category].filter(Boolean).join(' ').toLowerCase();

const matchesSearch = (tool, query, category = '') => {
  if (!query) return true;
  return searchableText(tool, category).includes(query);
};

const TypeTag = ({ type }) => {
  const style = toolTypeStyles[type] || toolTypeStyles.Calculator;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 999,
        px: 1.2,
        py: 0.45,
        bgcolor: style.bg,
        color: style.color,
        fontSize: '0.72rem',
        fontWeight: 800,
        lineHeight: 1,
      }}
    >
      {style.label}
    </Box>
  );
};

const FeaturedToolCard = ({ tool }) => {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardActionArea
        component={Link}
        to={tool.link}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        {tool.image && (
          <Box sx={{ position: 'relative', overflow: 'hidden' }}>
            <CardMedia
              component="img"
              height="140"
              image={tool.image}
              alt={tool.title}
              sx={{
                filter: 'brightness(0.94) contrast(0.96) saturate(0.92)',
              }}
            />
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(0deg, rgba(31, 41, 55, 0.34) 0%, rgba(107, 114, 128, 0.14) 42%, rgba(255, 255, 255, 0) 100%)',
              }}
            />
          </Box>
        )}
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.25 }}>
            <TypeTag type={tool.type} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {tool.category}
            </Typography>
          </Stack>
          <Typography variant="h6" component="div" sx={{ fontWeight: 400 }}>
            {tool.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {tool.description}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const CategoryCard = ({ category, query }) => {
  const Icon = category.icon;
  const categoryMatch = category.title.toLowerCase().includes(query);
  const tools = category.tools.filter((tool) => categoryMatch || matchesSearch(tool, query, category.title));

  if (!tools.length) return null;

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderColor: 'rgba(15, 23, 42, 0.08)',
        bgcolor: '#fff',
      }}
    >
      <Stack spacing={2} sx={{ p: 2.25, height: '100%' }}>
        <Stack direction="row" spacing={1.4} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              bgcolor: `${category.accent}16`,
              color: category.accent,
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" sx={{ color: '#111827', lineHeight: 1.2 }}>
              {category.title}
            </Typography>
            <Typography
              component={Link}
              to={`/apps?category=${encodeURIComponent(category.title)}`}
              variant="body2"
              sx={{ color: '#2563eb', fontWeight: 400, textDecoration: 'none' }}
            >
              View all
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1.1} sx={{ flex: 1 }}>
          {tools.slice(0, 3).map((tool) => (
            <Box
              component={Link}
              to={tool.link}
              key={tool.title}
              sx={{
                p: 1.15,
                borderRadius: 1,
                textDecoration: 'none',
                color: 'inherit',
                border: '1px solid rgba(15, 23, 42, 0.07)',
                bgcolor: '#f8fafc',
                transition: 'border-color 0.16s ease, background-color 0.16s ease',
                '&:hover': {
                  bgcolor: '#fff',
                  borderColor: category.accent,
                },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, color: '#111827', lineHeight: 1.25 }}>
                  {tool.title}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.7 }}>
                  <TypeTag type={tool.type} />
                </Stack>
              </Box>
            </Box>
          ))}
        </Stack>

      </Stack>
    </Card>
  );
};

const AppsIndex = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category') || 'All categories';
  const query = searchTerm.trim().toLowerCase();

  const handleCategoryChange = (value) => {
    if (value === 'All categories') {
      setSearchParams({});
      return;
    }
    setSearchParams({ category: value });
  };

  const filteredFeaturedTools = useMemo(
    () =>
      featuredTools.filter((tool) => {
        const categoryMatch =
          selectedCategory === 'All categories' ||
          selectedCategory === 'Featured' ||
          tool.category === selectedCategory;
        return categoryMatch && matchesSearch(tool, query);
      }),
    [query, selectedCategory]
  );

  const filteredCategories = useMemo(() => {
    if (selectedCategory === 'Featured') return [];
    return categories.filter((category) => {
      const categoryMatch = selectedCategory === 'All categories' || category.title === selectedCategory;
      if (!categoryMatch) return false;
      if (!query) return true;
      return category.title.toLowerCase().includes(query) || category.tools.some((tool) => matchesSearch(tool, query, category.title));
    });
  }, [query, selectedCategory]);

  const hasResults = filteredFeaturedTools.length > 0 || filteredCategories.length > 0;

  return (
    <>
      <Helmet>
        <title>Tools & Prompts for Real Financial Decisions | Talk About Finance</title>
        <meta
          name="description"
          content="Browse calculators and AI prompt generators to help you plan, decide, and feel more in control of your money."
        />
        <link rel="canonical" href="https://talkabout.finance/apps" />
      </Helmet>

      <Box sx={{ bgcolor: '#eef3f8', minHeight: '100vh', py: { xs: 3, md: 6 } }}>
        <Container maxWidth="xl">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: (theme) => theme.shadows[1],
              background: (theme) =>
                theme.palette.mode === 'light'
                  ? 'linear-gradient(135deg, #f7f9fc 0%, #ffffff 45%, #f7f9fc 100%)'
                  : 'linear-gradient(135deg, #1d1f24 0%, #17181c 45%, #1d1f24 100%)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(320px, 420px)' },
                gap: { xs: 2.5, md: 4 },
                alignItems: { xs: 'stretch', md: 'center' },
              }}
            >
              <Stack spacing={1.5}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 1 }}
                >
                  Tools & Apps
                </Typography>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{ fontWeight: 700 }}
                >
                  Tools & Prompts for Real Financial Decisions
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
                  Calculators and AI prompt generators to help you plan, decide, and feel more in control of your money.
                </Typography>
              </Stack>

              <Divider sx={{ display: { xs: 'block', md: 'none' } }} />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: 3,
                  alignItems: 'center',
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.72)',
                }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel id="tools-category-filter-label">Category</InputLabel>
                  <Select
                    labelId="tools-category-filter-label"
                    id="tools-category-filter"
                    value={selectedCategory}
                    label="Category"
                    onChange={(event) => handleCategoryChange(event.target.value)}
                  >
                    <MenuItem value="All categories">All categories</MenuItem>
                    {categoryOptions.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Search tools"
                  placeholder="Search by name, topic or type"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Box>
            </Box>
          </Paper>

          {hasResults ? (
            <Stack spacing={{ xs: 4, md: 5 }}>
              {filteredFeaturedTools.length > 0 && (
                <Box component="section">
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h5" gutterBottom>
                      Featured tools
                    </Typography>
                  </Stack>
                  <Grid container spacing={2.5}>
                    {filteredFeaturedTools.map((tool) => (
                      <Grid item xs={12} sm={6} lg={3} key={tool.title}>
                        <FeaturedToolCard tool={tool} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {selectedCategory === 'All categories' && !query && (
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '18px',
                    borderColor: 'rgba(147, 51, 234, 0.16)',
                    bgcolor: '#fff',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '54px 1fr auto' },
                      gap: 2,
                      alignItems: 'center',
                      p: { xs: 2.5, md: 3 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 54,
                        height: 54,
                        borderRadius: '16px',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: '#f3e8ff',
                        color: '#6b21a8',
                      }}
                    >
                      <AutoAwesome />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ color: '#111827', mb: 0.6 }}>
                        What are AI Prompt Generators?
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.65 }}>
                        Answer a few simple questions and we’ll create a powerful prompt you can use with ChatGPT, Claude, Gemini or any AI tool.
                      </Typography>
                    </Box>
                    <Button component={Link} to="/articles/what-are-ai-prompt-generators" variant="outlined" sx={{ fontWeight: 850, borderRadius: 999 }}>
                      Learn more →
                    </Button>
                  </Box>
                </Card>
              )}

              {filteredCategories.length > 0 && (
                <Box component="section">
                  <Typography variant="h5" gutterBottom>
                    Browse by category
                  </Typography>
                  <Grid container spacing={2.5}>
                    {filteredCategories.map((category) => (
                      <Grid item xs={12} sm={6} lg={3} key={category.title}>
                        <CategoryCard category={category} query={query} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Stack>
          ) : (
            <Card
              variant="outlined"
              sx={{
                borderRadius: '18px',
                borderColor: 'rgba(15, 23, 42, 0.08)',
                bgcolor: '#fff',
                p: { xs: 3, md: 5 },
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" sx={{ color: '#111827', mb: 1 }}>
                No tools found. Try a different search or category.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setSearchParams({});
                }}
                sx={{ mt: 1.5, fontWeight: 850, borderRadius: 999 }}
              >
                Reset filters
              </Button>
            </Card>
          )}
        </Container>
      </Box>
    </>
  );
};

export default AppsIndex;
